<template>
  <v-dialog
    v-model="modalState"
    width="950"
    persistent
    retain-focus
  >
    <v-card>
      <v-form
        ref="formResearchAmnio"
        v-model="formValid"
      >
        <v-card-title
          ref="researchTitle"
          class="sticky-header"
        >
          Amniotic Tissue Graft Research Information
        </v-card-title>
        <v-card-text class="pt-4 d-flex">
          <div class="d-flex text-left px-2 text-h6" style="background: #eff5fb; border-radius: 6px; width: 100%">
            <v-img
              :src="bioligicsLogo"
              max-width="70px"
              alt="logo"
              contain
              eager
              class="app-logo mx-2"
            ></v-img>
            <p class="mt-5" style="font-size: 18px">This patient has been selected to participate in
              a study on the efficacy of amniotic tissue grafts for wound treatment.</p>
          </div>
        </v-card-text>
        <div class="align-left pt-2 pl-5 d-flex">
          <icon-value
            :icon="icons.mdiCircle"
            :color="amnioResearchPatientType === 'control' ? '#16ce22' : '#ead600'"
          ></icon-value>
          <span
            class="text-uppercase font-weight-bold pl-1"
            :style="{ color: amnioResearchPatientType === 'control' ? '#16ce22' : '#ead600', 'line-height': '46px' }"
          >Working on</span>
        </div>
        <ResearchCardInfo
          :amnio-research-patient-type="amnioResearchPatientType"
          :patient-name="patientName"
          :facility-id="facilityId"
        >
        </ResearchCardInfo>
        <v-card-text class="pb-3 pt-6 pl-5" style="font-size: 18px">Please fill in the following additional information:</v-card-text>
        <DevelopedSymptoms
          v-model="amnio_research_symptoms_data"
        >
        </DevelopedSymptoms>
        <WoundSiteObservations
          v-model="amnio_research_data"
          :was-amnio-treated="wasAmnioTreated"
          :wound-site-pain-level.sync="wound_site_pain_level"
          :wound-site-odor.sync="wound_site_odor"
          :is-test-group="isTestGroup"
        >
        </WoundSiteObservations>
        <v-card-actions class="sticky-bottom">
          <!-- TODO: Update button functions -->
          <!-- cancel -->
          <btn
            label="Cancel"
            color="secondary"
            :icon="icons.mdiCancel"
            @click="cancel"
          ></btn>
          <!-- delete -->
          <btn
            label="Delete"
            color="error"
            :icon="icons.mdiTrashCan"
            @click="deleteInformation"
          ></btn>

          <v-spacer></v-spacer>

          <!-- save draft -->
          <btn
            label="Save Draft"
            :icon="icons.mdiContentSaveEdit"
            color="success"
            @click="save"
          ></btn>

          <!-- validate -->
          <btn
            label="Validate"
            :icon="icons.mdiCheckDecagram"
            @click="validate"
          ></btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import WoundSiteObservations from '@/components/notes/wound-detail/treatments/sections/AmnioResearchSections/WoundSiteObservations.vue'
import DevelopedSymptoms from '@/components/notes/wound-detail/treatments/sections/AmnioResearchSections/DevelopedSymptoms.vue'
import ResearchCardInfo from '@/components/notes/wound-detail/treatments/sections/AmnioResearchSections/ResearchInfoCard.vue'
import themeConfig from '@themeConfig'
import { v4 as uuidv4 } from 'uuid'
import {
  mdiCancel,
  mdiTrashCan,
  mdiContentSaveEdit,
  mdiCheckDecagram,
  mdiInformation,
  mdiCircle,
} from '@mdi/js'

const amniosResearchInitialData = {
  id: uuidv4(),
  treatment_id: null,
  redness_level: null,
  swelling_level: null,
  is_warmness: null,
  is_itching: null,
  discharge_type: null,
  discharge_amount: null,
  appearance: null,
  appearance_other: null,
}

export default {
  name: 'AmnioticResearchInformation',
  components: { DevelopedSymptoms, WoundSiteObservations, ResearchCardInfo },
  props: {
    modalState: {
      type: Boolean,
      default: false,
    },
    amnioResearch: {
      type: Object,
      default: () => {},
    },
    amnioResearchSymptoms: {
      type: Array,
      default: () => [],
    },
    patientName: {
      type: String,
      default: '',
    },
    amnioResearchPatientType: {
      type: String,
      default: '',
    },
    treatmentId: {
      type: String,
      default: '',
    },
    facilityId: {
      type: Number,
      default: null,
    },
    woundSitePainLevel: {
      type: Number,
      default: null,
    },
    woundSiteOdor: {
      type: Array,
      default: () => [],
    },
    wasAmnioTreated: {
      type: Boolean,
      default: false,
    },
    isTestGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      amnio_research_data: {
        ...amniosResearchInitialData,
        ...this.$lodash.cloneDeep(this.amnioResearch),
        treatment_id: this.treatmentId,
      },
      amnio_research_symptoms_data: this.amnioResearchSymptoms,
      patient_name: this.patientName,
      facility_id: this.facilityId,
      amnio_patient_research_type: this.amnioResearchPatientType,
      wound_site_pain_level: this.woundSitePainLevel,
      wound_site_odor: this.woundSiteOdor,
      icons: {
        mdiCancel,
        mdiTrashCan,
        mdiContentSaveEdit,
        mdiCheckDecagram,
        mdiInformation,
        mdiCircle,
      },
      bioligicsLogo: themeConfig.app.altLogo,
    }
  },
  computed: {
    ...mapGetters('baseData', ['woundProcedures']),
  },
  watch: {
    amnioResearch(newVal) {
      this.amnio_research_data = {
        ...amniosResearchInitialData,
        ...this.$lodash.cloneDeep(newVal),
        treatment_id: this.treatmentId,
      }
    },
    amnioResearchSymptoms(newVal) {
      this.amnio_research_symptoms_data = newVal
    },
    woundSiteOdor() {
      this.$emit('update:woundSiteOdor', this.wound_site_odor)
    },
    modalState(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const modalTitle = this.$refs.researchTitle
          if (modalTitle) modalTitle.scrollIntoView(true)
        })
      }
    },
  },
  methods: {
    async save() {
      const symptomsForDB = await this.prepSymptomsForDB()
      this.$emit('update:amnio-research', this.amnio_research_data)
      this.$emit('update:amnio-research-symptoms', symptomsForDB)
      this.$emit('update:wound-site-pain-level', this.wound_site_pain_level)
      this.$emit('update:wound-site-odor', this.wound_site_odor)
      this.$emit('update:amnio-research-form-valid', await this.validateForm())
      this.$emit('update:modal-state', false)

    },
    cancel() {
      this.$root.confirm({
        title: 'Discard Research Information Changes?',
        body: 'Are you sure you wish to discard research information changes?',
        cancel: 'No',
        confirm: 'Discard',
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.done()
        }
      })
    },
    async validate() {

      if (this.formValid) {
        await this.save()
        this.done()
      } else {
        this.$root.confirm({
          titleIconColor: 'error',
          title: 'Form Validation Failed',
          body: 'Some required answers are missing or invalid. Please review the form.',
          cancel: false,
        })
      }
    },
    deleteInformation() {
      this.$root.confirm({
        title: 'Confirm Deletion',
        subTitle: 'This action is irreversible.',
        body: 'Do you really want to delete this research information? This cannot be undone.',
        confirm: 'Delete',
        confirmIcon: this.icons.mdiTrashCan,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.$emit('update:amnio-research', {})
          this.$emit('update:amnio-research-symptoms', [])
          this.done()
        }
      })
    },
    done() {
      this.$emit('update:modal-state', false)
    },

    // Checkbox options need modifiction to save in DB
    prepSymptomsForDB() {
      let clonedSymptomsData = this.$lodash.cloneDeep(this.amnio_research_symptoms_data)

      if (clonedSymptomsData.length > 0) {
        clonedSymptomsData = clonedSymptomsData.map(item => ({
          treatment_id: this.treatmentId,
          symptom: item.symptom,
        }))
      }

      return clonedSymptomsData
    },
    validateForm() {
      return this.$refs.formResearchAmnio?.validate?.()
    },
  },
}
</script>
